import { useEffect, useState, type ReactNode } from 'react'
import { fetchSession, steamLoginUrl } from '../data/api'
import { SessionContext, type SessionInfo } from '../data/session'

// Access gate. Steam sign-in against the team roster is the only way in — the shared team password
// that stood here from 2026-07-16 was switched off and deleted on 31 Jul. Nothing here is a
// security boundary: the API verifies the session on every data route and this component only
// decides what to render. The bundle and static assets stay public by design; the data doesn't.
//
// However you got in, the session is published to the app so pages can stop offering buttons the
// API would refuse.

/** The API bounces a failed login back to `#/?login=<reason>` — someone arriving from Steam needs a
 *  sentence, not a 403 body. */
const LOGIN_ERRORS: Record<string, string> = {
  notrostered:
    'That Steam account isn’t on the team roster, so there’s nothing here for it yet — ask your coach to add you.',
  failed: 'Steam couldn’t confirm that sign-in. Try again.',
  // Not a failure so much as a sign-in that didn't begin in this browser: a login left half-finished
  // for a quarter of an hour, or a Steam link followed from somewhere other than here. Worded as
  // "start again" rather than anything alarming, because that is what it almost always is.
  expired:
    'That sign-in didn’t start here, or it sat unfinished too long. Use the button below to start again.',
}

function readLoginError(): string | null {
  const m = /[#&?]login=([a-z]+)/i.exec(window.location.hash + window.location.search)
  return m ? (LOGIN_ERRORS[m[1].toLowerCase()] ?? LOGIN_ERRORS.failed) : null
}

export function Gate({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionInfo | null>(null) // null = still asking the API
  const err = readLoginError()

  // The cookie outlives the tab, so an existing session shouldn't re-prompt.
  useEffect(() => {
    fetchSession().then(setSession)
  }, [])

  if (session === null) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-text-dim">…</div>
  }
  if (session.authed) {
    return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <div
        className="w-full max-w-sm border border-border bg-surface p-8"
        style={{ borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' }}
      >
        <div className="mb-6 flex items-center gap-3">
          <img
            src="/brand/jano-logo.png"
            alt="JANO"
            className="h-8 w-8"
            onError={(e) => (e.currentTarget.style.display = 'none')}
          />
          <div className="font-display text-lg font-bold">
            JANO <span className="text-accent">Demo Analyzer</span>
          </div>
        </div>

        {err && <p className="mb-4 text-xs leading-relaxed text-loss">{err}</p>}

        {session.methods.steam ? (
          <>
            <a
              href={steamLoginUrl()}
              className="font-display flex w-full items-center justify-center py-2.5 text-sm font-semibold uppercase tracking-wider"
              style={{ borderRadius: 'var(--radius)', background: 'var(--accent)', color: 'var(--accent-ink)' }}
            >
              Sign in through Steam
            </a>
            <p className="mt-2 text-[11px] leading-relaxed text-text-faint">
              Your Steam account is how the portal knows which numbers on the scoreboard are yours.
              Only accounts on the team roster can sign in — if it turns you away, ask your coach.
            </p>
          </>
        ) : (
          <p className="text-xs text-loss">
            Sign-in isn’t configured on this deployment — SITE_ORIGIN is missing from the API’s app
            settings.
          </p>
        )}
      </div>
    </div>
  )
}
